import React from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { List, ListSubheading, Menu, Button, Spinner, Icon } from '@oliasoft-open-source/react-ui-library';
import {
  operationSelected,
  operationsCleared,
  sectionSelected,
  reorderSections,
  showImportOperationModalUpdated,
  downloadToExcel,
  importOperationsFromExcel,
} from 'store/entities/activity-model/activity-model';
import { selectProjectOverview } from 'store/entities/projects/selectors';

import translations from 'src/internationalisation/translation-map.json';
import { getWellDetails } from 'store/entities/well-details/well-details';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { Badge } from '@oliasoft-open-source/react-ui-library';
import { round, unum } from '@oliasoft-open-source/units';
import { selectOperationList } from 'store/entities/activity-model/selectors';
import { meanAllActivities } from 'src/views/cost-setup/helpers/precalculate-time';

const SectionsList = ({
  activeSection,
  isAdding,
  isPageDisabled,
  setSelectedId,
  setSectionsDeleteModalVisible,
  sectionSelected,
  setActiveForm,
  sectionItems,
  reorderSections,
  showImportOperationModalUpdated,
  onClickAddSection,
  projectId,
  downloadToExcel,
  importOperationsFromExcel,
  projectOverview,
  operations,
  showMetrics,
  setShowMetrics,
}) => {
  const { t } = useTranslation();
  const onClickItem = (section) => {
    sectionSelected(section);
    setActiveForm('section');
  };
  const onClickDelete = (e, id) => {
    setSelectedId(e.shiftKey ? null : id);
    setSectionsDeleteModalVisible(true);
  };
  const disabled = isAdding || isPageDisabled;
  const items = sectionItems?.map((section) => {
    const { projectSectionId, name, sectionId, eventName } = section;

    const sectionOperations = operations.filter((op) => op.projectSectionId === projectSectionId);
    const operationCount = sectionOperations.length;
    const meanDays = round(
      unum(
        meanAllActivities(sectionOperations).reduce((sum, hours) => sum + hours, 0),
        'd',
        'h',
      ),
      2,
    );

    return {
      testId: `sections-list-${convertToKebabCase(name)}-item`,
      id: projectSectionId,
      name: name ?? t(translations.activityModel_newSection),
      onClick: () => onClickItem({ projectSectionId, name, sectionId }),
      active: projectSectionId === activeSection?.projectSectionId,
      draggable: true,
      actions: [
        ...(showMetrics
          ? [
              {
                childComponent: (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--padding-sm)',
                    }}
                  >
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        color: 'var(--color-text-success)',
                      }}
                    >
                      {`${new Intl.NumberFormat('en-US').format(meanDays)} ${t(translations.days)}`}
                    </span>
                    <Badge
                      title={String(operationCount)}
                      color={operationCount ? '#cccccc' : ''}
                      textColor={'#ffffff'}
                    />
                  </div>
                ),
              },
            ]
          : []),
        {
          testId: `sections-list-${convertToKebabCase(name)}-delete-button`,
          label: 'Delete',
          icon: 'delete',
          onClick: (e) => onClickDelete(e, projectSectionId),
          disabled,
        },
      ],
      metadata: eventName,
    };
  });

  const handleDownloadExcel = async (_projectOverview) => {
    const { name, projectId } = _projectOverview;
    const fileName = `${name}-activity-model`;
    await downloadToExcel(projectId, fileName);
  };

  const handleImportExcel = async (_projectId) => {
    const input = document.createElement('input');
    input.type = 'file';

    input.onchange = async (fileEvent) => {
      const file = fileEvent.target.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      await importOperationsFromExcel(formData, _projectId);
    };

    input.click();
  };

  const menuGetExcel = (
    <Menu
      menu={{
        trigger: 'Component',
        component: (
          <Button
            testId="activity-model-left-bar-more-button"
            small
            basic
            round
            colored="muted"
            icon="menu"
            disabled={disabled}
          />
        ),
        sections: [
          {
            testId: `activity-model-left-bar-${convertToKebabCase(t(translations.activityModel_addSection))}-option`,
            type: 'Option',
            icon: 'add',
            label: t(translations.activityModel_addSection),
            onClick: () => onClickAddSection(),
            disabled,
          },
          {
            testId: `activity-model-left-bar-${convertToKebabCase(
              t(translations.activityModel_deleteAllSections),
            )}-option`,
            type: 'Option',
            icon: 'delete',
            label: t(translations.activityModel_deleteAllSections),
            onClick: () => {
              setSelectedId(null);
              setSectionsDeleteModalVisible(true);
            },
            disabled,
          },
          {
            testId: `activity-model-left-bar-${convertToKebabCase(
              t(translations.activityModel_importFromExistingEstimate),
            )}-option`,
            type: 'Option',
            icon: 'import',
            label: t(translations.activityModel_importFromExistingEstimate),
            onClick: () => showImportOperationModalUpdated(true),
            disabled,
          },
        ],
      }}
    />
  );

  return (
    <>
      <List
        list={{
          name: <div data-testid="section-form-title">{t(translations.activityModel_title)}</div>,
          actions: [
            {
              testId: 'activity-model-left-bar-toggle-metrics-button',
              icon: showMetrics ? <Icon icon="info" color="var(--color-text-success)" /> : 'info',
              tooltip: showMetrics
                ? t(translations.activityModel_hideMeanTimeAndCounter)
                : t(translations.activityModel_showMeanTimeAndCounter),
              onClick: () => setShowMetrics((prev) => !prev),
            },
            {
              testId: 'activity-model-left-bar-download-button',
              icon: 'download',
              tooltip: t(translations.download),
              onClick: () => handleDownloadExcel(projectOverview),
            },
            {
              testId: 'activity-model-left-bar-upload-button',
              icon: 'upload',
              tooltip: t(translations.upload),
              onClick: () => handleImportExcel(projectId),
            },
            { childComponent: menuGetExcel },
          ],
          items: [],
        }}
      />
      <ListSubheading
        item={{
          testId: 'sections-list-subheading',
          name: t(translations.sections),
          actions: [
            {
              testId: 'sections-list-subheading-add-button',
              label: 'Add',
              icon: isAdding ? <Spinner tiny dark /> : 'add',
              disabled,
              primary: true,
              onClick: () => onClickAddSection(),
            },
          ],
        }}
      />
      <List
        list={{
          items,
        }}
        noHeader
        draggable={!isPageDisabled}
        onListReorder={(params) => reorderSections(params, sectionItems)}
      />
    </>
  );
};

const mapDispatchToProps = {
  getWellDetails,
  operationSelected,
  operationsCleared,
  sectionSelected,
  reorderSections,
  showImportOperationModalUpdated,
  downloadToExcel,
  importOperationsFromExcel,
};

const mapStateToProps = ({ entities }) => ({
  projectOverview: selectProjectOverview(entities),
  operations: selectOperationList({ entities }),
});

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(SectionsList));

export { Container as SectionsList };
